import { useState } from "react";
import { adminApi, API_BASE, isConfigured, partnerApi } from "./api";
import { adminToken, claimKeyFromUrl, partnerKey, signOut } from "./session";
import Admin from "./views/Admin";
import Cabinet from "./views/Cabinet";
import Login from "./views/Login";
import { Banner } from "./components/ui";

/**
 * Two routes, resolved from the pathname once at mount:
 *
 *   /admin   the team's board, unlocked by the shared ADMIN_TOKEN
 *   /        a partner's cabinet, unlocked by their own key
 *
 * There is no router library and no client-side navigation between them —
 * they are separate destinations for separate people, and each is opened
 * directly from a bookmark. Cloudflare Pages serves index.html for both via
 * public/_redirects.
 *
 * Both logins PROBE before storing. Accepting a credential into localStorage
 * and letting the first real request fail would bounce the user back here with
 * nothing to explain why; verifying it up front means a wrong key says so.
 */
export default function App() {
  const isAdminRoute =
    typeof window !== "undefined" && window.location.pathname.startsWith("/admin");

  // Runs exactly once, before the first render, so a key handed over in the
  // URL is in storage and out of the address bar before anything else looks
  // at either.
  const [claimed] = useState(() => claimKeyFromUrl());

  const [token, setToken] = useState<string | null>(() => adminToken.get());
  const [key, setKey] = useState<string | null>(() => claimed ?? partnerKey.get());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function forget() {
    signOut();
    setToken(null);
    setKey(null);
  }

  if (!isConfigured) {
    return (
      <div className="login">
        <h1>Panel not configured</h1>
        <Banner tone="error">
          This bundle was built without <code>VITE_API_BASE</code>, so it has no API to talk to.
          Set it in <code>panel/.env.production</code> (or in the shell that runs the build) to
          the Worker&apos;s URL, rebuild, and redeploy. See <code>panel/wrangler.toml</code> for
          why a runtime variable will not do.
        </Banner>
      </div>
    );
  }

  async function attempt(secret: string, probe: (secret: string) => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await probe(secret);
      return true;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "That did not work. Check the value and try again.",
      );
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (isAdminRoute) {
    if (token) return <Admin token={token} onSignOut={forget} />;

    return (
      <Login
        title="Solex partner panel"
        blurb="Team access. Paste the admin token."
        fieldLabel="Admin token"
        placeholder="••••••••••••"
        error={error}
        busy={busy}
        onSubmit={async (secret) => {
          if (await attempt(secret, (s) => adminApi.overview(s))) {
            adminToken.set(secret);
            setToken(secret);
          }
        }}
        footer={
          <>
            Are you a partner? <a href="/">Open your cabinet</a>.
          </>
        }
      />
    );
  }

  if (key) return <Cabinet apiKey={key} onSignOut={forget} />;

  return (
    <Login
      title="Your Solex partner cabinet"
      blurb="Paste the access key we sent you to see your referral link and how it's doing."
      fieldLabel="Access key"
      placeholder="your access key"
      error={error}
      busy={busy}
      onSubmit={async (secret) => {
        if (await attempt(secret, (s) => partnerApi.me(s))) {
          partnerKey.set(secret);
          setKey(secret);
        }
      }}
      footer={
        <>
          Lost your key? Ask us for a new one — keys are stored hashed and cannot be looked up.
          <br />
          <span className="tiny">Connected to {API_BASE.replace(/^https?:\/\//, "")}</span>
        </>
      }
    />
  );
}
